import { Component, type ReactNode, type ErrorInfo } from 'react'
import { Link } from 'react-router-dom'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center bg-[#0A0A0A]">
          {/* S6 badge */}
          <div className="w-14 h-14 rounded-2xl bg-[#E8850C] flex items-center justify-center shadow-[0_0_40px_rgba(232,133,12,0.35)] mb-6">
            <span className="text-white text-xl font-bold tracking-tight">S6</span>
          </div>

          <h1 className="text-2xl font-bold text-[#F5F5F5] mb-3">Something went wrong</h1>
          <p className="text-[#6B6B6B] text-sm max-w-md mb-2">
            An unexpected error occurred. Our team has been notified.
          </p>
          {this.state.error && (
            <p className="text-[#3A3A3A] text-xs font-mono mb-8 max-w-sm truncate">
              {this.state.error.message}
            </p>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={this.handleReset}
              className="h-9 px-4 rounded-lg text-sm font-medium bg-[#E8850C] text-white hover:bg-[#D4760A] transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/catalog"
              onClick={this.handleReset}
              className="h-9 px-4 rounded-lg text-sm font-medium bg-[#141414] border border-[#1E1E1E] text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#E8850C] transition-colors flex items-center"
            >
              Back to Catalog
            </Link>
          </div>

          <p className="text-[#3A3A3A] text-xs mt-10">
            Built by{' '}
            <a
              href="https://s6labs.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#E8850C] hover:underline"
            >
              S6 Labs
            </a>
          </p>
        </div>
      )
    }

    return this.props.children
  }
}
